import Thumbnail from "@marginly/ui/components/thumbnail";
import T from "@marginly/ui/components/typography";
import React, { useState } from "react";

import { getTokenLargeImagePath } from "@/components/common/token";
import { useAprMap } from "@/components/util/use-apr-map";
import { usePoolsStateMap } from "@/components/util/use-pools-state-map";
import { useWeb3Context } from "@/contexts/web3";
import { UserPointsResponse } from "@/types/api";
import { formatPercent } from "@/util/formatters";
import { sortByTotal } from "@/util/sort-by-total";

import {
  PoolTitleLeft,
  PoolWrapper,
  PoolWrapperTitle,
  ThumbnailWrapper,
} from "../../common/accordion";
import { FarmUnit } from "../farm-unit";
import { Pool, Section as SectionType } from "../types";
import { getAdditionalPoolInfo } from "../utils/get-additional-pool-Info";
import { PoolCardsContainer } from "./styled";

interface Props {
  chainName: string;
  userPoints: UserPointsResponse | undefined;
  pools: Pool[];
  section: SectionType;
  priceByToken?: Record<`0x${string}`, number>;
}

export function Section({
  chainName,
  userPoints,
  pools,
  section,
  priceByToken,
}: Props) {
  const [isPoolDetails, setIsPoolDetails] = useState(true);
  const { getPoolByAddress } = useWeb3Context();

  const poolsIds = pools.map((pool) => pool.address).filter(Boolean);
  const poolsApr = useAprMap(poolsIds);
  const poolsTotal = usePoolsStateMap(poolsIds);

  const renderThumbnailImage = () => {
    if (section.id === "pendle") {
      return "/images/token/pendle.svg";
    }
    if (section.id === "rewards") {
      return "/images/gift-b.svg";
    }
    return "";
  };

  const aprList = Object.entries(poolsApr || {}).map(
    ([, apr]) => apr.farmingBaseApy,
  );

  const maxApy = Math.max(...aprList, 0);

  const poolsInfo = pools
    .map((pool) => {
      const additionalPoolInfo = getAdditionalPoolInfo({
        pool,
        poolInfo: getPoolByAddress(pool.address),
        poolsApr,
        poolsTotal,
        priceByToken,
      });
      if (!additionalPoolInfo) return null;
      return {
        ...pool,
        ...additionalPoolInfo,
      };
    })
    .filter((pool) => !!pool);

  const sortedPools = sortByTotal(poolsInfo);

  return (
    <PoolWrapper className="colored">
      <PoolWrapperTitle onClick={() => setIsPoolDetails(!isPoolDetails)}>
        <PoolTitleLeft>
          <ThumbnailWrapper>
            <Thumbnail md accent className="">
              <img src={renderThumbnailImage()} alt="" />
            </Thumbnail>
            <img src="/images/greenspark.svg" alt="" className="sparkling" />
          </ThumbnailWrapper>
          <div>
            <T headerS>{section.title}</T>
            <T caption secondary>
              Up to{" "}
              {formatPercent(maxApy / 100, {
                maximumFractionDigits: 1,
              })}{" "}
              APY
            </T>
          </div>
        </PoolTitleLeft>

        <img
          src="/images/arrow-down.svg"
          alt=""
          style={{
            transform: isPoolDetails ? "rotate(180deg)" : "",
            transition: "200ms ease-out",
            filter: "invert(var(--svg-invert))",
          }}
        />
      </PoolWrapperTitle>
      {isPoolDetails && (
        <PoolCardsContainer>
          {sortedPools.map((pool) => {
            return (
              <FarmUnit
                key={pool.address}
                chainName={chainName}
                symbol={pool.baseTokenSymbol}
                poolAddress={pool.address}
                apy={pool.apy || 0}
                icon={getTokenLargeImagePath(pool.baseTokenSymbol)}
                userPoints={userPoints}
                maturityDate={pool.maturityDate}
                totalCollateral={pool.totalCollateral}
                totalDebt={pool.totalDebt}
              />
            );
          })}
        </PoolCardsContainer>
      )}
    </PoolWrapper>
  );
}
